/**
 * Symptom Guide Reference Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Stethoscope, CheckCircle, Phone, AlertTriangle } from "lucide-react";

export function SymptomGuideOverlay({ patientId, patientName, onSave, onClose }) {
  const [selectedSymptom, setSelectedSymptom] = useState("pain");
  
  const symptomGuides = [
    {
      id: "pain",
      name: "Pain",
      icon: "🔴",
      description: "Pain is a common symptom in hospice care. Effective management improves quality of life.",
      comfort_measures: [
        "Position patient comfortably with pillows for support",
        "Apply heat or cold as preferred by patient",
        "Gentle massage to non-painful areas",
        "Distraction techniques (music, conversation)",
        "Administer PRN medications as ordered",
        "Keep room quiet and dimly lit if preferred"
      ],
      when_to_call: [
        "Pain not relieved by medication within 1 hour",
        "Pain score increases significantly (e.g., from 4 to 7+)",
        "New location of pain develops",
        "Pain interferes with sleep or eating",
        "Patient expresses distress about pain control"
      ],
      call_immediately: [
        "Sudden severe pain (9-10/10)",
        "Pain with new neurological symptoms (weakness, numbness)",
        "Pain with difficulty breathing",
        "Uncontrolled pain causing extreme distress"
      ]
    },
    {
      id: "nausea",
      name: "Nausea/Vomiting",
      icon: "🤢",
      description: "Nausea can significantly impact comfort. Multiple approaches may be needed.",
      comfort_measures: [
        "Small, frequent meals instead of large ones",
        "Avoid strong odors - open windows if helpful",
        "Cool, bland foods often better tolerated",
        "Ginger ale or peppermint tea may help",
        "Rest after eating, but don't lie flat",
        "Apply cool cloth to forehead"
      ],
      when_to_call: [
        "Nausea persists despite medication",
        "Unable to keep liquids down for 6+ hours",
        "Vomiting more than 3 times in 24 hours",
        "Signs of dehydration (dry mouth, dark urine)"
      ],
      call_immediately: [
        "Vomiting blood or coffee-ground material",
        "Severe abdominal pain with vomiting",
        "Signs of severe dehydration",
        "New confusion with vomiting"
      ]
    },
    {
      id: "dyspnea",
      name: "Shortness of Breath",
      icon: "💨",
      description: "Breathlessness can be frightening. Calm presence and interventions help.",
      comfort_measures: [
        "Elevate head of bed or use pillows",
        "Fan blowing gently toward face",
        "Open window for fresh air if desired",
        "Stay calm - anxiety worsens breathlessness",
        "Pursed lip breathing technique",
        "Administer oxygen as ordered",
        "Loose, comfortable clothing"
      ],
      when_to_call: [
        "Increased difficulty breathing at rest",
        "Needing to sit up to breathe",
        "Breathing faster than usual",
        "More anxious about breathing"
      ],
      call_immediately: [
        "Sudden severe shortness of breath",
        "Blue coloring around lips or fingernails",
        "Can't speak in full sentences",
        "Gasping or struggling to breathe"
      ]
    },
    {
      id: "anxiety",
      name: "Anxiety/Agitation",
      icon: "😰",
      description: "Anxiety is common and treatable. Both medication and comfort measures help.",
      comfort_measures: [
        "Calm, reassuring presence",
        "Reduce stimulation (noise, lights, visitors)",
        "Slow, deep breathing together",
        "Gentle hand holding or touch if welcomed",
        "Soft music or nature sounds",
        "Maintain familiar routine",
        "Spiritual support if desired"
      ],
      when_to_call: [
        "Anxiety not improved with medication",
        "New or worsening confusion",
        "Patient unable to rest or sleep",
        "Expressing new fears or concerns"
      ],
      call_immediately: [
        "Severe agitation or combativeness",
        "Sudden personality change",
        "Hallucinations causing distress",
        "Risk of harm to self or others"
      ]
    },
    {
      id: "constipation",
      name: "Constipation",
      icon: "🔒",
      description: "Very common with pain medications. Prevention is key.",
      comfort_measures: [
        "Administer stool softeners as ordered",
        "Encourage fluids if able to swallow",
        "Gentle abdominal massage (clockwise)",
        "Privacy and time for bowel movements",
        "Prune juice or warm liquids",
        "Activity as tolerated"
      ],
      when_to_call: [
        "No bowel movement in 3+ days",
        "Abdominal bloating or discomfort",
        "Nausea related to constipation",
        "Straining without results"
      ],
      call_immediately: [
        "Severe abdominal pain",
        "Vomiting with no bowel movements",
        "Blood in stool",
        "Fecal impaction suspected"
      ]
    },
    {
      id: "secretions",
      name: "Secretions/Death Rattle",
      icon: "🫁",
      description: "Noisy breathing from secretions is common at end of life. Usually not distressing to patient.",
      comfort_measures: [
        "Position on side to allow drainage",
        "Elevate head slightly",
        "Gentle mouth care with swabs",
        "Avoid deep suctioning (may cause more secretions)",
        "Administer medications as ordered",
        "Reassure family this is normal",
        "Keep lips moist with balm"
      ],
      when_to_call: [
        "Secretions causing visible distress to patient",
        "New fever or change in secretion color",
        "Significant increase in secretions"
      ],
      call_immediately: [
        "Patient showing signs of respiratory distress",
        "Choking or inability to clear airway"
      ]
    }
  ];

  const currentGuide = symptomGuides.find(s => s.id === selectedSymptom);

  return (
    <div className="space-y-4 p-4" data-testid="symptom-guide-overlay">
      <div className="flex items-center gap-2 mb-4">
        <Stethoscope className="w-5 h-5 text-teal-400" />
        <h3 className="font-semibold text-white">Symptom Management Guide</h3>
        {patientName && <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>}
      </div>

      <p className="text-sm text-slate-400">Quick reference for managing common hospice symptoms</p>

      {/* Symptom Selector */}
      <div className="flex flex-wrap gap-2">
        {symptomGuides.map(symptom => (
          <Button
            key={symptom.id}
            variant={selectedSymptom === symptom.id ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedSymptom(symptom.id)}
            className={selectedSymptom === symptom.id ? "bg-teal-600" : "border-slate-600 text-slate-300"}
          >
            <span className="mr-1">{symptom.icon}</span>
            {symptom.name}
          </Button>
        ))}
      </div>

      {/* Selected Symptom Guide */}
      {currentGuide && (
        <ScrollArea className="h-[400px] pr-2">
          <div className="space-y-4">
            {/* Description */}
            <div className="p-3 bg-teal-900/20 border border-teal-500/30 rounded-lg">
              <p className="text-sm text-teal-300">{currentGuide.description}</p>
            </div>

            {/* Comfort Measures */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-4 h-4" />Comfort Measures
              </Label>
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <ul className="space-y-2">
                  {currentGuide.comfort_measures.map((measure, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs text-green-400">{idx + 1}</span>
                      </div>
                      <span className="text-slate-300">{measure}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* When to Call */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-amber-400">
                <Phone className="w-4 h-4" />When to Call Hospice
              </Label>
              <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                <ul className="space-y-2">
                  {currentGuide.when_to_call.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 mt-1.5" />
                      <span className="text-amber-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Call Immediately */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-4 h-4" />CALL IMMEDIATELY If...
              </Label>
              <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                <ul className="space-y-2">
                  {currentGuide.call_immediately.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 mt-1.5" />
                      <span className="text-red-200 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </ScrollArea>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Close</Button>
      </div>
    </div>
  );
}

export default SymptomGuideOverlay;
